import $api from 'http';
import $apiJSON from 'http/apiJSON';
import axios from 'axios';
import { API_URL } from 'http';
import { notifyRequestChange } from './RequestCountEvents';
import { normalizeVehicleNumber, normalizeVehiclePayload } from './VehicleNumberService';

/**
 * Check if a vehicle number is already registered (no auth required).
 */
export async function checkVehicleNumber(vehicleNumber) {
  return axios.get(`${API_URL}tabel/check-vehicle-number/`, {
    params: { vehicle_number: normalizeVehicleNumber(vehicleNumber) },
  });
}

/**
 * Check if an INN is already registered (no auth required).
 */
export async function checkInnExists(inn) {
  return axios.post(`${API_URL}employees/check-inn/`, { inn });
}

/**
 * Register a new driver (no auth required).
 * formData must be a FormData instance with all fields + files.
 */
export async function registerDriver(formData, config = {}) {
  return axios.post(`${API_URL}tabel/driver-register/`, normalizeVehiclePayload(formData), {
    ...config,
    headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
  });
}

export async function registerDriverAdmin(formData, config = {}) {
  return $api.post('/tabel/driver-register-admin/', normalizeVehiclePayload(formData), config).then(notifyRequestChange);
}

/** Get the current user's DriverChecked status + Driver data */
export async function getDriverCabinet() {
  return $api.get('/tabel/driver-cabinet/');
}

/** Get the current driver's loads — active and history (paginated) */
export async function getDriverMyLoads(params) {
  return $api.get('/tabel/driver-my-loads/', { params });
}

/** Lightweight tab badge counts (always-on poll) */
export async function getTabCounts() {
  return $api.get('/tabel/tab-counts/');
}

/** Get drivers assigned to the current operator's base */
export async function getBaseDrivers() {
  return $api.get('/tabel/base-drivers/');
}

/** Remove a driver from the operator's base */
export async function removeDriverFromBase(driverId, actorPin) {
  return $apiJSON.post(`/tabel/base-drivers/${driverId}/remove/`, { actor_pin: String(actorPin ?? '') });
}

/** Get the current user's DriverChecked registration status */
export async function getDriverCheckedMy() {
  return $api.get('/tabel/driver-checked/my/');
}

/** Resubmit registration after rejection */
export async function resubmitDriverChecked(formData) {
  return $api.put('/tabel/driver-checked/my/', normalizeVehiclePayload(formData), {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(notifyRequestChange);
}

/** Create a base join/leave request */
export async function createBaseRequest(data) {
  return $api.post('/tabel/base-request/', data).then(notifyRequestChange);
}

/** Get driver's own base requests */
export async function getMyBaseRequests() {
  return $api.get('/tabel/base-requests/my/');
}

// ── Admin ──────────────────────────────────────────────

/** Admin: list all DriverChecked registrations */
export async function getDriverCheckedList(statusFilter = null, extraParams = {}) {
  const p = new URLSearchParams();
  if (statusFilter) p.set('status', statusFilter);
  Object.entries(extraParams).forEach(([k, v]) => p.set(k, v));
  const qs = p.toString() ? `?${p}` : '';
  return $api.get(`/tabel/driver-checked/${qs}`);
}

/** Admin: approve a DriverChecked registration */
export async function approveDriverChecked(id) {
  return $api.post(`/tabel/driver-checked/${id}/approve/`).then(notifyRequestChange);
}

/** Admin: reject a DriverChecked registration */
export async function rejectDriverChecked(id, reason) {
  return $api.post(`/tabel/driver-checked/${id}/reject/`, { reason }).then(notifyRequestChange);
}

/** Admin: list all base requests */
export async function getBaseRequestsAdmin(statusFilter = null, extraParams = {}) {
  const p = new URLSearchParams();
  if (statusFilter) p.set('status', statusFilter);
  Object.entries(extraParams).forEach(([k, v]) => p.set(k, v));
  const qs = p.toString() ? `?${p}` : '';
  return $api.get(`/tabel/base-requests/admin/${qs}`);
}

// ── Operator ───────────────────────────────────────────

export async function getBaseRequestsForOperator(extraParams = {}) {
  return $api.get('/tabel/base-requests/operator/', { params: extraParams });
}

/** Approve a base request */
export async function approveBaseRequest(id) {
  return $api.post(`/tabel/base-requests/${id}/approve/`).then(notifyRequestChange);
}

/** Reject a base request */
export async function rejectBaseRequest(id, reason) {
  return $api.post(`/tabel/base-requests/${id}/reject/`, { reason }).then(notifyRequestChange);
}

// ── Doc change requests ──────────────────────────────────

/** Driver: submit a doc change request (FormData: field_name, field_label, new_file) */
export async function createDocChangeRequest(formData) {
  return $api.post('/tabel/doc-change-request/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(notifyRequestChange);
}

/** Driver: get own doc change requests */
export async function getMyDocChangeRequests() {
  return $api.get('/tabel/doc-change-request/my/');
}

/** Admin: list doc change requests */
export async function getDocChangeRequestsAdmin(statusFilter = null, extraParams = {}) {
  const p = new URLSearchParams();
  if (statusFilter) p.set('status', statusFilter);
  Object.entries(extraParams).forEach(([k, v]) => p.set(k, v));
  const qs = p.toString() ? `?${p}` : '';
  return $api.get(`/tabel/doc-change-request/admin/${qs}`);
}

/** Admin: approve a doc change request */
export async function approveDocChangeRequest(id) {
  return $api.post(`/tabel/doc-change-request/${id}/approve/`).then(notifyRequestChange);
}

/** Admin: reject a doc change request */
export async function rejectDocChangeRequest(id, reason) {
  return $api.post(`/tabel/doc-change-request/${id}/reject/`, { reason }).then(notifyRequestChange);
}
